import React, {useState, useEffect, useCallback} from "react";
import {toast} from "react-toastify";
import ConfirmationDialog from "../shared/ConfirmationDialog";
import EditSubscriptionModal from "./EditSubscriptionModal";
import {
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Autocomplete,
  IconButton,
  Menu,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Collapse,
  Tooltip,
  Popover,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {CreateSubscriptionRequest, Subscription} from "../models/subscription";
import {useGetProductsQuery} from "../../app/services/product";
import {
  useGetProductSubscriptionsQuery,
  useCreateSubscriptionMutation,
  useDeleteSubscriptionMutation,
  useGetSubscriptionFeaturesQuery,
} from "../../app/services/subscription";
import { useNavigate } from 'react-router-dom';

const FeaturePreviewChip: React.FC<{ subscriptionId: string }> = ({ subscriptionId }) => {
  const { data, isLoading, isError } = useGetSubscriptionFeaturesQuery(subscriptionId, { skip: !subscriptionId });
  const count = data?.length ?? 0;
  let title: string = '';
  if (isLoading) title = 'Loading features…';
  else if (isError) title = 'Failed to load features';
  else if (!count) title = 'No features';
  else {
    const list = (data ?? []) as any[];
    const names = list.slice(0, 6).map((f: any) => f.featureDescription || f.featureName).filter(Boolean);
    title = names.join(', ');
    if (count > names.length) title = `${title}${names.length ? ', ' : ''}+${count - names.length} more`;
  }
  return (
    <Tooltip title={title} placement="top" arrow>
      <Chip label={`${count} ${count === 1 ? 'feature' : 'features'}`} size="small" color="primary" variant="outlined" />
    </Tooltip>
  );
};

const FeaturesInlineList: React.FC<{ subscriptionId: string }> = ({ subscriptionId }) => {
  const { data, isLoading, isError } = useGetSubscriptionFeaturesQuery(subscriptionId, { skip: !subscriptionId });
  if (isLoading) return <Typography variant="caption" color="text.secondary">Loading features…</Typography>;
  if (isError) return <Typography variant="caption" color="error">Failed to load features.</Typography>;
  const list = (data ?? []) as any[];
  if (!list.length) return <Typography variant="caption" color="text.secondary">No features.</Typography>;
  return (
    <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.5}>
      {list.slice(0, 12).map((f: any) => {
        const key = f.id ?? f.productFeatureId ?? `${f.featureName}`;
        const label = f.featureDescription || f.featureName || key;
        return <Chip key={key} size="small" variant="outlined" label={label} />;
      })}
      {list.length > 12 && (
        <Chip size="small" label={`+${list.length - 12} more`} />
      )}
    </Stack>
  );
};

const SubscriptionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newSubscription, setNewSubscription] =
    useState<CreateSubscriptionRequest>({
      name: "",
      productId: "",
      description: "",
    });

  const [showViewFeatures, setShowViewFeatures] = useState(false);
  const [selectedSubscription, setSelectedSubscription] =
    useState<Subscription | null>(null);
  const [featuresProductName, setFeaturesProductName] = useState<string | undefined>(undefined);
  
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState<{
    id: string;
    productName?: string;
    subscriptionName?: string;
  } | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [subscriptionToEdit, setSubscriptionToEdit] =
    useState<Subscription | null>(null);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<
    string | null
  >(null);
  const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);
  const infoOpen = Boolean(infoAnchorEl);
  const openInfo = (e: React.MouseEvent<HTMLElement>) => setInfoAnchorEl(e.currentTarget);
  const closeInfo = () => setInfoAnchorEl(null);
  /*
   * API calls
   * */
  const {
    data: productSubscriptions,
    isLoading,
    refetch: refetchSubscriptions,
  } = useGetProductSubscriptionsQuery();
  const [createSubscription, {isLoading: isCreating}] =
    useCreateSubscriptionMutation();

  const {
    data: productsData,
    isSuccess: productIsSuccess,
  } = useGetProductsQuery(
    { page: 1, pageSize: 500 },
    {
      refetchOnMountOrArgChange: true,
    },
  );
  const [deleteSubscription] = useDeleteSubscriptionMutation();

  const {
    data: subscriptionFeatures,
    isLoading: featuresLoading,
    error: featuresError,
  } = useGetSubscriptionFeaturesQuery(selectedSubscriptionId!, {
    skip: !selectedSubscriptionId, // Only fetch when ID is set
  });

  /*
   * Effects
   * */
  useEffect(() => {
    if (productIsSuccess && productsData?.data) {
      const filteredProducts = productsData.data.filter(
        (p: {productName: string}) => p.productName !== "IDMSPortal",
      );
      setProducts(filteredProducts);
    }
  }, [productIsSuccess, productsData]);

  const handleSubscriptionNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setNewSubscription(prev => ({...prev, name: e.target.value}));
    },
    [],
  );

  const handleSubscriptionProductChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setNewSubscription(prev => ({...prev, productId: e.target.value}));
    },
    [],
  );

  const handleCreateSubscription = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSubscription.name.trim() || !String(newSubscription.productId))
      return;

    try {
      await createSubscription({
        name: newSubscription.name.trim(),
        productId: newSubscription.productId,
        description: newSubscription.description?.trim() || '',
      }).unwrap();

      setNewSubscription({name: "", productId: "", description: ""});
      setShowCreateForm(false);
      toast.success("Subscription tier created successfully!");
      refetchSubscriptions();
    } catch (err: any) {
      setError("Failed to create subscription. Please try again.");
      toast.error("Failed to create subscription. Please try again.");
    }
  };

  const handleDeleteSubscription = (id: string, productName?: string, subscriptionName?: string) => {
    setSubscriptionToDelete({ id, productName, subscriptionName });
    setShowDeleteConfirm(true);
  };

  const confirmDeleteSubscription = async () => {
    if (!subscriptionToDelete) return;

    try {
      await deleteSubscription(subscriptionToDelete.id).unwrap();
      toast.success("Subscription tier deleted successfully");
      setShowDeleteConfirm(false);
      setSubscriptionToDelete(null);
      refetchSubscriptions();
    } catch (err) {
      console.error("Error deleting subscription:", err);
      const anyErr: any = err;
      const status = anyErr?.response?.status as number | undefined;
      const data = anyErr?.response?.data;
      let message = "Failed to delete subscription tier.";
      if (status === 409) {
        message =
          (data?.title as string) ||
          "Cannot delete subscription tier because it is in use. Remove dealer assignments first.";
      } else if (status === 400) {
        message =
          typeof data === "string" ? data : (data?.title as string) || message;
      } else if (status && status >= 500) {
        message =
          "Server error while deleting subscription tier. It may have active dealer assignments. Please try again or remove assignments first.";
      }
      toast.error(message);
      setShowDeleteConfirm(false);
      setSubscriptionToDelete(null);
    }
  };

  const cancelDeleteSubscription = () => {
    setShowDeleteConfirm(false);
    setSubscriptionToDelete(null);
  };

  const handleViewFeatures = (subscription: Subscription, productName?: string) => {
    setSelectedSubscription(subscription);
    setSelectedSubscriptionId(subscription.subscriptionId);
    setFeaturesProductName(productName);
    setShowViewFeatures(true);
  };

  const [editProductName, setEditProductName] = useState<string | undefined>(undefined);

  const openEditSubscription = (subscription: Subscription, productName?: string) => {
    setSubscriptionToEdit(subscription);
    setEditProductName(productName);
    setShowEditModal(true);
    setError(null);
  };

  const closeEditSubscription = () => {
    setShowEditModal(false);
    setSubscriptionToEdit(null);
  };

  const handleSavedSubscription = async (_updated: Subscription) => {
    closeEditSubscription();
  };

  const getTierColor = (tierName: string) => {
    switch (tierName.toLowerCase()) {
      case "gold":
        return "#ffd700";
      case "silver":
        return "#c0c0c0";
      case "bronze":
        return "#cd7f32";
      default:
        return "#667eea";
    }
  };

  const groupedSubscriptions = React.useMemo(() => {
    if (!productSubscriptions) return {};
    const grouped: {
      [key: string]: {
        productId: string;
        subscriptions: {
          productSubscriptionId: string;
          subscriptionName: string;
        }[];
      };
    } = {};
    productSubscriptions.forEach(prod => {
      grouped[prod.productName] = {
        productId: prod.productId,
        subscriptions: prod.subscriptions,
      };
    });
    return grouped;
  }, [productSubscriptions]);

  const productOptions = React.useMemo(() =>
    Object.entries(groupedSubscriptions).map(([name, { productId }]) => ({ label: name, id: productId })),
    [groupedSubscriptions]
  );
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pids = params.get('productId');
    const q = params.get('q');
    if (pids) setSelectedProductIds(pids.split(',').filter(Boolean));
    if (q) setSearch(q);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (selectedProductIds.length > 0) params.set('productId', selectedProductIds.join(','));
    else params.delete('productId');
    if (search.trim()) params.set('q', search.trim());
    else params.delete('q');
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState(null, '', newUrl);
  }, [selectedProductIds, search]);

  const filteredEntries = React.useMemo(() => {
    const includesProduct = (pid: string) => selectedProductIds.length === 0 || selectedProductIds.includes(pid);
    const q = search.trim().toLowerCase();
    return Object.entries(groupedSubscriptions)
      .map(([productName, info]) => {
        if (!includesProduct(info.productId)) return null;
        const subs = info.subscriptions.filter(s => {
          if (!q) return true;
          return (
            s.subscriptionName.toLowerCase().includes(q) ||
            productName.toLowerCase().includes(q)
          );
        });
        if (subs.length === 0) return null;
        return [productName, { ...info, subscriptions: subs }] as [string, typeof info];
      })
      .filter(Boolean) as [string, { productId: string; subscriptions: { productSubscriptionId: string; subscriptionName: string; }[] }][];
  }, [groupedSubscriptions, selectedProductIds, search]);

  const [expandedMap, setExpandedMap] = useState<Record<string, boolean>>({});
  useEffect(() => {
    const key = 'subscriptionsAccordionExpanded';
    const saved = window.localStorage.getItem(key);
    let initial: Record<string, boolean> = {};
    if (saved) {
      try { initial = JSON.parse(saved) || {}; } catch {}
    }
    const map: Record<string, boolean> = { ...initial };
    Object.values(groupedSubscriptions).forEach(({ productId }) => {
      if (typeof map[productId] === 'undefined') map[productId] = true;
    });
    setExpandedMap(map);
  }, [groupedSubscriptions]);
  useEffect(() => {
    const key = 'subscriptionsAccordionExpanded';
    window.localStorage.setItem(key, JSON.stringify(expandedMap));
  }, [expandedMap]);

  const toggleExpanded = (productId: string) => (_e: any, isExpanded: boolean) => {
    setExpandedMap(prev => ({ ...prev, [productId]: isExpanded }));
  };

  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});
  const toggleCard = (id: string) => setExpandedCards((prev: Record<string, boolean>) => ({ ...prev, [id]: !prev[id] }));
  const bulkToggleCard = (ids: string[], expand: boolean) => setExpandedCards((prev: Record<string, boolean>) => {
    const next = { ...prev } as Record<string, boolean>;
    ids.forEach((id) => { next[id] = expand; });
    return next;
  });

  const appTopOffset = 'var(--app-top-offset, 64px)';
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const baseBarRef = React.useRef<HTMLDivElement | null>(null);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayLeft, setOverlayLeft] = useState(0);
  const [overlayWidth, setOverlayWidth] = useState<string | number>('100%');
  useEffect(() => {
    let scheduled = false;
    const onMeasure = () => {
      const el = baseBarRef.current; if (!el) return;
      const r = el.getBoundingClientRect();
      setOverlayLeft(r.left);
      setOverlayWidth(r.width);
    };
    const onScroll = () => {
      const sent = sentinelRef.current; if (!sent) return;
      const rect = sent.getBoundingClientRect();
      setOverlayVisible(rect.top <= (parseInt(String(getComputedStyle(document.documentElement).getPropertyValue('--app-top-offset') || '64'), 10) || 64));
      if (!scheduled) {
        scheduled = true;
        requestAnimationFrame(() => { scheduled = false; onMeasure(); });
      }
    };
    onMeasure();
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true } as any);
    window.addEventListener('resize', onMeasure);
    const ro = new ResizeObserver(onMeasure);
    if (baseBarRef.current) ro.observe(baseBarRef.current);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onMeasure); ro.disconnect(); };
  }, []);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuContext, setMenuContext] = useState<{
    productName: string;
    productId: string;
    subscription: { productSubscriptionId: string; subscriptionName: string };
  } | null>(null);

  const openMenu = (event: React.MouseEvent<HTMLElement>, ctx: { productName: string; productId: string; subscription: { productSubscriptionId: string; subscriptionName: string } }) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuContext(ctx);
  };
  const closeMenu = () => { setMenuAnchorEl(null); setMenuContext(null); };

  if (isLoading) {
    return (
      <Box sx={{ width: '100%', py: 2 }}>
        <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'divider' }}>
          <Skeleton variant="rounded" width={220} height={36} />
        </Stack>

        {[0, 1].map((idx) => (
          <Box key={idx} sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 2, boxShadow: 1, mb: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'divider' }}>
              <Skeleton variant="text" width={180} height={28} />
              <Skeleton variant="rounded" width={160} height={28} />
            </Stack>
            <Grid container spacing={2}>
              {[0, 1, 2].map((i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardHeader
                      avatar={<Skeleton variant="circular" width={40} height={40} />}
                      title={<Skeleton variant="text" width="60%" />}
                    />
                    <CardContent>
                      <Stack spacing={1}>
                        <Skeleton variant="rounded" width={120} height={32} />
                        <Skeleton variant="rounded" width={100} height={32} />
                        <Skeleton variant="rounded" width={90} height={32} />
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', py: 2 }}>
      <Box ref={sentinelRef} sx={{ height: 1, mt: -1 }} />
      <Box ref={baseBarRef} sx={{ mb: 1 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Autocomplete
            multiple
            options={productOptions}
            getOptionLabel={(o) => o.label}
            value={productOptions.filter(o => selectedProductIds.includes(o.id))}
            onChange={(_e, value) => setSelectedProductIds(value.map(v => v.id))}
            size="small"
            renderInput={(params) => <TextField {...params} label="Filter by product" placeholder="Products" />}
            sx={{ minWidth: 240, flexShrink: 0 }}
          />
          <TextField
            size="small"
            label="Search"
            placeholder="Search subscriptions or products"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, maxWidth: 520 }}
          />
          <IconButton
            aria-label="Clear filters"
            onClick={() => { setSelectedProductIds([]); setSearch(''); }}
            disabled={selectedProductIds.length === 0 && !search.trim()}
            sx={{ ml: { xs: 0, sm: 1 } }}
          >
            <FilterAltOffIcon />
          </IconButton>
          <Tooltip title="About add-ons">
            <IconButton size="small" aria-label="About add-ons" onClick={openInfo}>
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" onClick={() => setShowCreateForm(true)} sx={{ ml: { xs: 0, sm: 'auto' } }}>Add Subscription Tier</Button>
        </Stack>
      </Box>
      {overlayVisible && (
        <Box
          sx={{
            position: 'fixed',
            top: appTopOffset,
            left: `${overlayLeft}px`,
            width: typeof overlayWidth === 'number' ? `${overlayWidth}px` : overlayWidth,
            zIndex: (theme) => theme.zIndex.appBar + 2,
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(6px)',
            pt: 1,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            boxShadow: 2,
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ px: 2 }}>
            <Autocomplete
              multiple
              options={productOptions}
              getOptionLabel={(o) => o.label}
              value={productOptions.filter(o => selectedProductIds.includes(o.id))}
              onChange={(_e, value) => setSelectedProductIds(value.map(v => v.id))}
              size="small"
              renderInput={(params) => <TextField {...params} label="Filter by product" placeholder="Products" />}
              sx={{ minWidth: 240, flexShrink: 0 }}
            />
            <TextField
              size="small"
              label="Search"
              placeholder="Search subscriptions or products"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1, maxWidth: 520 }}
            />
            <IconButton
              aria-label="Clear filters"
              onClick={() => { setSelectedProductIds([]); setSearch(''); }}
              disabled={selectedProductIds.length === 0 && !search.trim()}
              sx={{ ml: { xs: 0, sm: 1 } }}
            >
              <FilterAltOffIcon />
            </IconButton>
            <Tooltip title="About add-ons">
              <IconButton size="small" aria-label="About add-ons" onClick={openInfo}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button variant="contained" onClick={() => setShowCreateForm(true)} sx={{ ml: { xs: 0, sm: 'auto' } }}>Add Subscription Tier</Button>
          </Stack>
        </Box>
      )}

      <Popover
        open={infoOpen}
        anchorEl={infoAnchorEl}
        onClose={closeInfo}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 1.5, maxWidth: 380 }}>
          <Typography variant="body2">
            Add-on features are listed in the <Button size="small" variant="text" onClick={() => { closeInfo(); navigate('/features'); }}>Features Catalog</Button>
            Assign add-ons to dealers in <Button size="small" variant="text" onClick={() => { closeInfo(); navigate('/dealers'); }}>Dealers</Button> (under dealer details). They do not appear on this Subscription Tiers page.
          </Typography>
        </Box>
      </Popover>

      {error && (
        <Box sx={{ mb: 2, p: 2, borderRadius: 1, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.light' }}>
          <Typography color="error">Failed to load subscriptions. Please try again.</Typography>
        </Box>
      )}

      <Dialog open={showCreateForm} onClose={() => setShowCreateForm(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create New Subscription Tier</DialogTitle>
        <form onSubmit={handleCreateSubscription}>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                label="Subscription Name"
                value={newSubscription.name}
                onChange={handleSubscriptionNameChange}
                placeholder="e.g., Gold, Silver, Bronze"
                required
                fullWidth
              />
              <TextField
                label="Description"
                value={newSubscription.description}
                onChange={(e) => setNewSubscription(prev => ({ ...prev, description: e.target.value }))}
                inputProps={{ maxLength: 500 }}
                placeholder="Short description for this tier (max 500 chars)"
                multiline
                minRows={3}
                fullWidth
              />
              <FormControl fullWidth required>
                <InputLabel id="productSelectLabel">Product</InputLabel>
                <Select
                  labelId="productSelectLabel"
                  id="productSelect"
                  label="Product"
                  value={newSubscription.productId}
                  onChange={(e) => handleSubscriptionProductChange(e as any)}
                  displayEmpty={false}
                >
                  {products.map(product => (
                    <MenuItem key={product.id} value={String(product.id)}>
                      {product.productName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCreateForm(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={isCreating}>{isCreating ? 'Creating...' : 'Create Subscription Tier'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={showViewFeatures && !!selectedSubscription} onClose={() => { setShowViewFeatures(false); setSelectedSubscription(null); setFeaturesProductName(undefined); }} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack direction="row" alignItems="center" gap={1}>
            {selectedSubscription?.name} Features
            {!!selectedSubscription && (
              <Chip size="small" label={featuresProductName || 'Unknown Product'} sx={{ ml: 'auto' }} />
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" alignItems="center" gap={2} sx={{ mb: 2 }}>
            {featuresLoading ? (
              <Skeleton variant="rounded" width={120} height={28} />
            ) : (
              <Chip label={`${subscriptionFeatures?.length ?? 0} features`} color="primary" variant="outlined" />
            )}
          </Stack>
          {featuresError && <Typography color="error" variant="body2">Failed to load features.</Typography>}
          {featuresLoading && (
            <List dense>
              {[0, 1, 2, 3].map((i) => (
                <ListItem key={i} disableGutters>
                  <ListItemText primary={<Skeleton variant="text" width="70%" />} />
                </ListItem>
              ))}
            </List>
          )}
          {!featuresLoading && (!subscriptionFeatures || subscriptionFeatures.length === 0) && (
            <Typography variant="body2" color="text.secondary">No features assigned.</Typography>
          )}
          {!!subscriptionFeatures && subscriptionFeatures.length > 0 && (
            <List dense>
              {subscriptionFeatures.map((feature) => (
                <ListItem key={feature.id} disableGutters>
                  <ListItemText primary={feature.featureDescription || feature.featureName} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowViewFeatures(false); setSelectedSubscription(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {filteredEntries.length > 0 ? (
          filteredEntries.map(([productName, { productId, subscriptions }]) => (
            <Accordion key={productId} expanded={!!expandedMap[productId]} onChange={toggleExpanded(productId)} sx={{ bgcolor: 'background.paper', mb: 0.5, '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                  <Typography variant="subtitle1" fontWeight={600}>{productName}</Typography>
                  <Chip label={`${subscriptions.length} tier${subscriptions.length !== 1 ? 's' : ''}`} size="small" color="primary" variant="outlined" />
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ mb: 0.5 }}>
                  {(() => { const ids = subscriptions.map(s => s.productSubscriptionId); return (
                    <>
                      <Button size="small" onClick={() => bulkToggleCard(ids, true)}>Expand all</Button>
                      <Button size="small" onClick={() => bulkToggleCard(ids, false)}>Collapse all</Button>
                    </>
                  ); })()}
                </Stack>
                <Grid container spacing={1}>
                  {subscriptions.map((subscription) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={subscription.productSubscriptionId}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardHeader
                          sx={{ py: 0.5, '& .MuiCardHeader-content': { overflow: 'hidden' } }}
                          avatar={<Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: getTierColor(subscription.subscriptionName), color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>💎</Box>}
                          title={<Typography variant="subtitle1" fontWeight={700} noWrap>{subscription.subscriptionName}</Typography>}
                          action={
                            <IconButton size="small" aria-label="actions" onClick={(e) => openMenu(e, { productName, productId, subscription })}>
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          }
                        />
                        <CardContent sx={{ pt: 0.25, pb: 0.75 }}>
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <FeaturePreviewChip subscriptionId={subscription.productSubscriptionId} />
                            <IconButton
                              size="small"
                              aria-label="Toggle features preview"
                              onClick={() => toggleCard(subscription.productSubscriptionId)}
                            >
                              <ExpandMoreIcon fontSize="small" sx={{ transform: expandedCards[subscription.productSubscriptionId] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 180ms ease' }} />
                            </IconButton>
                          </Stack>
                          <Collapse in={!!expandedCards[subscription.productSubscriptionId]} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 1, maxHeight: 180, overflow: 'auto', pr: 0.5 }}>
                              <FeaturesInlineList subscriptionId={subscription.productSubscriptionId} />
                            </Box>
                          </Collapse>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>No Subscriptions Found</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Create subscription tiers to organize your product features</Typography>
            <Button variant="contained" onClick={() => setShowCreateForm(true)}>Create Subscription</Button>
          </Box>
        )}
      </Box>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={() => {
          if (!menuContext) return; const { productId, productName, subscription } = menuContext;
          const mapped: Subscription = { subscriptionId: subscription.productSubscriptionId, name: subscription.subscriptionName, productId };
          handleViewFeatures(mapped, productName);
          closeMenu();
        }}>View Features</MenuItem>
        <MenuItem onClick={() => {
          if (!menuContext) return; const { productId, productName, subscription } = menuContext;
          const mapped: Subscription = { subscriptionId: subscription.productSubscriptionId, name: subscription.subscriptionName, productId };
          openEditSubscription(mapped, productName);
          closeMenu();
        }}>Edit</MenuItem>
        <MenuItem onClick={() => {
          if (!menuContext) return; const { productName, subscription } = menuContext;
          handleDeleteSubscription(subscription.productSubscriptionId, productName, subscription.subscriptionName);
          closeMenu();
        }}>Delete</MenuItem>
      </Menu>

      <EditSubscriptionModal
        isOpen={showEditModal}
        subscription={subscriptionToEdit}
        onClose={closeEditSubscription}
        onSaved={handleSavedSubscription}
        refreshSubscriptions={refetchSubscriptions}
        productName={editProductName}
      />

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        title="Delete Subscription"
        message={`Are you sure you want to delete this subscription? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteSubscription}
        onCancel={cancelDeleteSubscription}
        isDangerous={true}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" label={subscriptionToDelete?.productName || 'Unknown Product'} />
          {subscriptionToDelete?.subscriptionName && (
            <Chip size="small" color="default" label={subscriptionToDelete.subscriptionName} />
          )}
        </Stack>
      </ConfirmationDialog>
    </Box>
  );
};

export default SubscriptionsPage;
